import React, { Component } from "react";
import clsx from "clsx";
import { ISiteWidget, getCustomWidgetId, IEditableWidget } from "../../models/cms";
import EventsCalendar from "../events/EventsCalendar";
import FormsList from "../siteForms/FormsList";
import FormSiteContentPage from "../siteForms/FormSiteContent";
import FormWidget from "../siteForms/FormWidget";
import EditorContent from "./root/EditorContent";
import Quotes from "./root/Quotes";
import Directors from "./root/Directors";
import GalleryWidget from "../gallery/GalleryWidget";
import HonorUProfiles from "../honoru/HonorUProfiles";
import { IPage, ICMSPage } from "../../laco-common/base/models/page";
import HomeEvents from "../events/HomeEvents";
import HonorUFeatured from "../honoru/HonorUFeatured";
import CustomWidget from "./root/CustomWidget";
import { UICMSContent } from "../ui/UICMSContent";
import StreamsList from "../streams/StreamsList";
import StreamPage from "../streams/StreamPage";
import Officers from "./root/Officers";
import DonateBtn from "../donate/DonateBtn";
import StreamsPreview from "../streams/StreamsPreview";
import { DigitalAccess } from "../streams/DigitalAccess";
import AudioWidget from "../audio/AudioWidget";
import StorePage from "../ecwid/StorePage";
import SubscriptionCheckout from "../ecwid/SubscriptionCheckout";
import Facilities from "../peer-support/facilities";
import Agents from "../peer-support/agents";
import AdminMonthlyReports from "../peer-support/AdminMonthlyReports";
import PeerSupport from "../peer-support";
import Home from "../peer-support/Home";
import ContactsList from "./root/ContactsList";
import Ads from "../market/Ads";
import UserRoleContent from "../auth/permissions/UserRoleContent";
import PSFormSubmissions from "../peer-support/PSFormSubmissions";
import FormSubmissions from "../siteForms/FormSubmissions";
import { DynamicLoadComponent } from "../../DynamicLoadComponent";
import Messages from "../market/Messages";
import { PaymentForm } from "../stripe/PaymentForm";
import CMSRoleProvider from "../../admin/contexts/cms-role/CMSRoleProvider";
import { MembershipWidget } from "../bw/MembershipWidget";
import { BenefitsWidget } from "../bw/Benefits";
import { AboutBlueWidget } from "../bw/AboutBlue";
import { HomeBlueWidget } from "../bw/HomeBlue";
import { MentalHealthResourcesWidget } from "../bw/MentalHealthResources";
import { FirewireAppWidget } from "../bw/FirewireApp";
import SocialsWidget from "../bw/Socials";
import { Donate } from "../donate/Donate";

export type WidgetProps = {
	widget: ISiteWidget;
	page: IPage;
	isNested?: boolean;
	idPage?: boolean;
	inHeader?: boolean;
	widgetNo?: number;
} & IEditableWidget;

export type WidgetsProps = {
	widgets: ISiteWidget[];
	page: IPage;
	isNested?: boolean;
	idPage?: boolean;
};

export const Widget: React.FC<WidgetProps> = (props: WidgetProps) => {
	const widget: ISiteWidget = props.widget;
	// console.debug("widget:", widget);
	if (widget.component) return <widget.component {...(widget.componentProps || {})} />;
	switch (widget.widgetType) {
		case "checkout":
			return <SubscriptionCheckout />;
		case "donate":
			return <Donate widget={widget} page={props.page} isNested={props.isNested} {...widget.widgetProps} />;
		case "editor-content":
			return <EditorContent widget={widget} page={props.page} inHeader={props.inHeader} />;
		case "cms-content":
			return (
				<div
					id={`${props.page?.id}-cms-content`}
					className={clsx(
						!props.isNested && "container",
						!props.page?.noPadding && "section-padding",
						props.page?.omitContentPaddingTop && "pt-0",
						props.page?.omitContentPaddingBottom && "pb-0"
					)}
				>
					<UICMSContent content={props.page?.content} />
					{props.page?.donateUrl && <DonateBtn url={props.page?.donateUrl} />}
				</div>
			);
		case "events-calendar":
			return <EventsCalendar widget={widget} isNested={props.isNested} page={props.page} />;
		case "upcoming-events":
			return <HomeEvents />;
		case "forms-list":
			return <FormsList widget={widget} isNested={props.isNested} page={props.page} />;
		case "form":
			return (
				<FormWidget
					formId={
						widget.params ||
						(Array.isArray(props.page?.formId)
							? props.page?.formId[props.widgetNo]
							: props.page?.formId || props.page?.psFormId)
					}
					standalone={true}
					isNested={props.isNested}
					page={props.page}
					{...widget.widgetProps}
				/>
			);
		case "form-content-page":
		case "form-content-page2":
			return (
				<FormSiteContentPage
					page={props.page}
					{...widget.widgetProps}
					widgetType={widget.widgetType}
					widgetNo={props.widgetNo}
				/>
			);
		case "digital-access":
			return (
				<DigitalAccess
					widget={widget}
					page={props.page}
					isNested={props.isNested}
					idPage={props.idPage}
					{...widget.widgetProps}
				/>
			);
		case "gallery":
			return (
				<GalleryWidget widget={widget} page={props.page} isNested={props.isNested} {...widget.widgetProps} />
			);
		case "audio":
			return <AudioWidget widget={widget} page={props.page} isNested={props.isNested} {...widget.widgetProps} />;
		case "honoru":
			return <HonorUProfiles widget={widget} page={props.page} isNested={props.isNested} />;
		case "honoru-featured":
			return <HonorUFeatured widget={widget} page={props.page} isNested={props.isNested} />;
		case "officers":
			return <Officers widget={widget} isNested={props.isNested} page={props.page} />;
		case "directors":
			return <Directors isNested={props.isNested} page={props.page} widget={null} />;
		case "membership":
			return <MembershipWidget widget={widget} isNested={props.isNested} page={props.page} />;
		case "benefits":
			return <BenefitsWidget widget={widget} isNested={props.isNested} page={props.page} />;
		case "aboutBlue":
			return <AboutBlueWidget widget={widget} isNested={props.isNested} page={props.page} />;
		case "homeBlue":
			return <HomeBlueWidget widget={widget} isNested={props.isNested} page={props.page} />;
		case "firewire-app":
			return <FirewireAppWidget widget={widget} isNested={props.isNested} page={props.page} />;
		case "socials":
			return <SocialsWidget widget={widget} isNested={props.isNested} page={props.page} />;
		case "mental-health-resources":
			return <MentalHealthResourcesWidget widget={widget} isNested={props.isNested} page={props.page} />;
		case "streams-preview":
			return <StreamsPreview widget={widget} page={props.page} />;
		case "streams":
			if (props.idPage) return <StreamPage widget={widget} page={props.page} />;
			// return <Streams widget={widget} preview={widget.widgetType === "streams-preview"} />;
			return <StreamsList widget={widget} page={props.page} />;
		case "quotes":
			return <Quotes widget={widget} page={props.page} isNested={props.isNested} />;
		case "store":
			return <StorePage isNested={props.isNested} />;
		case "form-submissions":
			if ((props.page as ICMSPage)?.useFirewire)
				return (
					<PSFormSubmissions
						isNested={props.isNested}
						widget={null}
						fullWidth={props.page?.fullWidth}
						page={props.page}
					/>
				);
			return (
				<CMSRoleProvider>
					<FormSubmissions
						isNested={props.isNested}
						widget={null}
						fullWidth={props.page?.fullWidth}
						page={props.page}
					/>
				</CMSRoleProvider>
			);
		case "peer-support":
			return <PeerSupport isNested={true} widget={null} page={null} />;
		case "ps-home":
			return <Home isNested={true} widget={null} page={null} />;
		case "ps-facilities":
			return <Facilities />;
		case "ps-agents":
			return <Agents />;
		case "ps-admin-monthly-reports":
			return <AdminMonthlyReports />;
		case "ps-form-submissions":
			return <PSFormSubmissions page={props.page} />;
		case "marketplace":
		case "marketplace-preview":
			return (
				<UserRoleContent roles={["user", "admin"]} noAuthMessage={true}>
					<Ads isNested={props.isNested} widget={widget} page={props.page} />
				</UserRoleContent>
			);
		case "messages":
		case "messages-preview":
			return (
				<UserRoleContent roles={["user", "admin"]} noAuthMessage={true}>
					<Messages isNested={props.isNested} widget={widget} page={props.page} />
				</UserRoleContent>
			);
		case "cms-editor":
		case "cms-edit-collection":
		case "cms-forms":
		case "cms-file-storage":
		case "cms-users":
		case "cms-pages": {
			return <DynamicLoadComponent path="./admin/CMSWidget" componentProps={{ page: props.page, widget }} />;
		}
		case "stripe-payment": {
			return <PaymentForm isNested={props.isNested} widget={widget} page={props.page} />;
		}
		default:
			const widgetType: string = widget && widget.widgetType ? widget.widgetType : "";
			if (widgetType && widgetType.startsWith("collection-")) {
				const collectionName: string = widgetType.substr(11);
				return (
					<ContactsList
						collectionName={collectionName}
						isNested={props.isNested}
						page={props.page}
						widget={null}
					/>
				);
			}
			if (widgetType.startsWith("custom-widget-")) {
				const customWidgetId: string = getCustomWidgetId(widgetType);
				return <CustomWidget id={customWidgetId} isNested={props.isNested} page={props.page} widget={null} />;
			}
			return null;
	}
};

export const Widgets: React.FC<WidgetsProps> = (props: WidgetsProps) => {
	if (!props.widgets || !props.widgets.length) return null;
	return (
		<>
			{props.widgets.map((widget: ISiteWidget, i: number) => (
				<Widget
					key={i}
					widget={widget}
					widgetNo={i}
					page={props.page}
					isNested={props.isNested}
					idPage={props.idPage}
				/>
			))}
		</>
	);
};
